import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { useSEO, ORG_JSONLD } from "@/lib/seo";
import { useFeatured, useSettings, useIsMobile, useSold } from "@/lib/hooks";
import { mediaUrl, WHATSAPP_DISPLAY, WHATSAPP_LINK } from "@/lib/api";
import { coverOf } from "@/lib/format";
import { ParallaxImage, Reveal, Stagger, StaggerItem, Tilt } from "@/components/site/Motion";
import { VehicleCard } from "@/components/site/VehicleCard";
import { NetworkSection } from "@/components/site/NetworkSection";
import { ContactBlock } from "@/components/site/ContactBlock";

const Hero = ({ s }) => {
  const { t, bi } = useLang();
  const ref = useRef(null);
  const isMobile = useIsMobile();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", isMobile ? "0%" : "18%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const video = (isMobile && s.hero_video_mobile_url) || s.hero_video_url;
  const poster = mediaUrl((isMobile && s.hero_video_mobile_url && s.hero_poster_mobile_url) || s.hero_poster_url || s.hero_image_url);
  return (
    <section ref={ref} data-testid="hero" className="relative h-[100svh] min-h-[600px] overflow-hidden bg-[#070809]">
      <motion.div style={{ y }} className="absolute inset-0">
        {video ? (
          <video
            key={video}
            data-testid="hero-video"
            className="h-full w-full object-cover"
            src={mediaUrl(video)}
            poster={poster}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            disablePictureInPicture
          />
        ) : (
          <img src={poster} alt="" className="h-full w-full object-cover" />
        )}
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#070809] via-[#070809]/20 to-[#070809]/30" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#070809]/55 via-transparent to-transparent" />
      <motion.div style={{ opacity }} className="container-x relative flex h-full flex-col justify-end pb-24 md:pb-28">
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.8 }} className="eyebrow mb-6">
          {bi(s.hero_subtitle)}
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45, duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="display max-w-5xl text-[2.6rem] leading-[0.95] sm:text-6xl lg:text-[5.5rem]"
          data-testid="hero-title"
        >
          {bi(s.hero_title)}
        </motion.h1>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8, duration: 0.8 }} className="mt-6 max-w-xl text-base text-[#dfe3e8] md:text-lg" data-testid="hero-tagline">
          {bi(s.hero_tagline)}
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.8 }} className="mt-10 flex flex-wrap items-center gap-3">
          <Link to="/vehicles" data-testid="hero-cta-explore" className="btn btn-primary">{t("hero_explore")} <ArrowRight size={14} /></Link>
          <Link to="/sourcing" data-testid="hero-cta-sourcing" className="btn btn-ghost">{t("hero_source")}</Link>
          <Link to="/sell" data-testid="hero-cta-sell" className="mono link-underline ml-1 text-[0.68rem] text-[#c7ccd4] hover:text-white">{t("hero_sell")}</Link>
        </motion.div>
        <motion.a
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3, duration: 0.8 }}
          href={WHATSAPP_LINK} target="_blank" rel="noreferrer" data-testid="hero-whatsapp"
          className="mono mt-8 inline-flex w-fit items-center gap-3 text-[0.68rem] text-[#c7ccd4] hover:text-white"
        >
          <span className="status-dot bg-[#25d366]" /> WhatsApp {WHATSAPP_DISPLAY}
        </motion.a>
      </motion.div>
      <div className="mono absolute bottom-8 right-24 hidden items-center gap-3 text-[0.6rem] text-[#8b95a5] md:flex">
        <span>{t("hero_scroll")}</span>
        <span className="block h-10 w-px bg-gradient-to-b from-[#8b95a5] to-transparent" />
      </div>
    </section>
  );
};

const Positioning = () => {
  const { t } = useLang();
  const items = [["pos_1", "pos_1d"], ["pos_2", "pos_2d"], ["pos_3", "pos_3d"]];
  return (
    <section className="section light relative overflow-hidden" data-testid="positioning-section">
      <div className="container-x grid gap-14 lg:grid-cols-12">
        <Reveal className="lg:col-span-7">
          <p className="eyebrow mb-8">{t("pos_eyebrow")}</p>
          <h2 className="display-tight text-2xl leading-snug text-[#0e1013] sm:text-3xl lg:text-[2.6rem]">{t("pos_title")}</h2>
        </Reveal>
        <Stagger className="lg:col-span-5 lg:pt-16">
          {items.map(([k, d], i) => (
            <StaggerItem key={k} className="grid grid-cols-[2.5rem_1fr] gap-4 border-t border-[#0e1013]/15 py-6 last:border-b">
              <span className="mono text-[0.6rem] text-[#5b6572]">0{i + 1}</span>
              <div>
                <p className="display-tight text-lg text-[#0e1013]">{t(k)}</p>
                <p className="mt-2 text-sm leading-relaxed text-[#4b5563]">{t(d)}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
};

const Featured = () => {
  const { t } = useLang();
  const { data = [], isLoading } = useFeatured();
  if (isLoading) return null;
  if (!data.length) {
    return (
      <section className="section bg-[#070809]" data-testid="featured-section">
        <div className="container-x grid gap-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <p className="eyebrow mb-5">{t("featured_eyebrow")}</p>
            <h2 className="display text-3xl sm:text-4xl lg:text-5xl">{t("featured_title")}</h2>
            <p className="mt-8 max-w-lg text-base leading-relaxed text-[#c7ccd4]" data-testid="featured-empty-message">{t("home_opp_msg")}</p>
          </Reveal>
          <Reveal delay={0.1} className="flex flex-col gap-3 lg:col-span-4 lg:col-start-9 lg:justify-end">
            <Link to="/vehicles" data-testid="featured-empty-cta-view" className="btn btn-ghost">{t("home_opp_view")}</Link>
            <Link to="/sourcing" data-testid="featured-empty-cta-sourcing" className="btn btn-primary">{t("hero_source")} <ArrowRight size={14} /></Link>
          </Reveal>
        </div>
      </section>
    );
  }
  const [first, ...rest] = data;
  return (
    <section className="section bg-[#070809]" data-testid="featured-section">
      <div className="container-x">
        <Reveal className="mb-12 flex flex-wrap items-end justify-between gap-6 md:mb-16">
          <div>
            <p className="eyebrow mb-5">{t("featured_eyebrow")}</p>
            <h2 className="display text-3xl sm:text-4xl lg:text-5xl">{t("featured_title")}</h2>
          </div>
          <Link to="/vehicles" data-testid="featured-view-all" className="mono link-underline inline-flex items-center gap-2 text-[0.68rem] text-[#c7ccd4] hover:text-white">
            {t("featured_all")} <ArrowUpRight size={14} />
          </Link>
        </Reveal>
        <div className="grid gap-10 lg:grid-cols-12 lg:gap-8">
          <Reveal className="lg:col-span-7"><VehicleCard v={first} large /></Reveal>
          <div className="grid gap-10 sm:grid-cols-2 lg:col-span-5 lg:grid-cols-1 lg:gap-10 lg:pt-24">
            {rest.slice(0, 2).map((v, i) => (
              <Reveal key={v.id} delay={0.1 * (i + 1)}><VehicleCard v={v} /></Reveal>
            ))}
          </div>
        </div>
        {rest.length > 2 && (
          <div className="mt-10 grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
            {rest.slice(2, 5).map((v, i) => <Reveal key={v.id} delay={0.08 * i}><VehicleCard v={v} /></Reveal>)}
          </div>
        )}
      </div>
    </section>
  );
};

const Cinematic = ({ s }) => {
  const { t } = useLang();
  const video = s.section_video_url;
  return (
    <section className="relative h-[70vh] min-h-[420px] overflow-hidden" data-testid="cinematic-section">
      {video ? (
        <video className="absolute inset-0 h-full w-full object-cover" src={mediaUrl(video)} autoPlay muted loop playsInline preload="metadata" />
      ) : (
        <ParallaxImage src={mediaUrl(s.visuals?.cinematic)} alt="" className="absolute inset-0" strength={12} />
      )}
      <div className="absolute inset-0 bg-[#070809]/35" />
      <div className="container-x relative flex h-full items-end pb-16 md:pb-20">
        <Reveal><p className="display-tight max-w-3xl text-2xl text-white sm:text-3xl lg:text-4xl">{t("cinematic_quote")}</p></Reveal>
      </div>
    </section>
  );
};

const SourcingTeaser = ({ s }) => {
  const { t, bi } = useLang();
  return (
    <section className="section light" data-testid="sourcing-section">
      <div className="container-x grid items-center gap-12 lg:grid-cols-12">
        <Reveal className="order-2 lg:order-1 lg:col-span-5">
          <p className="eyebrow mb-6">{t("sourcing_eyebrow")}</p>
          <h2 className="display text-3xl text-[#0e1013] sm:text-4xl lg:text-5xl">{t("sourcing_title")}</h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-[#4b5563]">{t("sourcing_copy")}</p>
          <Link to="/sourcing" data-testid="sourcing-cta" className="btn btn-dark mt-10">{bi(s.sourcing_cta)} <ArrowRight size={14} /></Link>
        </Reveal>
        <Tilt className="order-1 lg:order-2 lg:col-span-7" max={3}>
          <Reveal className="hover-zoom aspect-[4/3] overflow-hidden lg:aspect-[16/10]">
            <img src={mediaUrl(s.visuals?.sourcing)} alt="" loading="lazy" className="h-full w-full object-cover" />
          </Reveal>
        </Tilt>
      </div>
    </section>
  );
};

const SellTeaser = ({ s }) => {
  const { t } = useLang();
  return (
    <section className="relative overflow-hidden bg-[#0e1013]" data-testid="sell-section">
      <div className="grid lg:grid-cols-2">
        <ParallaxImage src={mediaUrl(s.visuals?.sell)} alt="" className="aspect-[4/3] lg:aspect-auto lg:min-h-[640px]" strength={8} />
        <div className="section container-x flex flex-col justify-center lg:pl-20">
          <Reveal>
            <p className="eyebrow mb-6">{t("sell_eyebrow")}</p>
            <h2 className="display text-3xl sm:text-4xl lg:text-5xl">{t("sell_title")}</h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-[#c7ccd4]">{t("sell_copy")}</p>
            <Link to="/sell" data-testid="sell-cta" className="btn btn-primary mt-10 w-fit">{t("sell_cta")}</Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const AboutTeaser = ({ s }) => {
  const { t } = useLang();
  return (
    <section className="section light" data-testid="about-section">
      <div className="container-x grid gap-12 lg:grid-cols-12">
        <Reveal className="lg:col-span-4"><div className="hover-zoom aspect-[3/4] overflow-hidden"><img src={mediaUrl(s.visuals?.about)} alt="" loading="lazy" className="h-full w-full object-cover" /></div></Reveal>
        <Reveal delay={0.1} className="flex flex-col justify-center lg:col-span-7 lg:col-start-6">
          <p className="eyebrow mb-6">{t("about_eyebrow")}</p>
          <p className="display-tight text-xl leading-snug text-[#0e1013] sm:text-2xl lg:text-3xl">{t("about_copy")}</p>
          <Link to="/about" data-testid="about-cta" className="mono link-underline mt-10 inline-flex w-fit items-center gap-2 text-[0.68rem] text-[#0e1013]">{t("about_more")} <ArrowUpRight size={14} /></Link>
        </Reveal>
      </div>
    </section>
  );
};

const RecentlySold = () => {
  const { t } = useLang();
  const { data = [] } = useSold();
  if (!data.length) return null;
  return (
    <section className="border-t border-white/10 bg-[#0b0d10] py-14 md:py-20" data-testid="sold-section">
      <div className="container-x">
        <Reveal className="mb-8 flex items-end justify-between gap-6">
          <div>
            <p className="eyebrow mb-4">{t("sold_eyebrow")}</p>
            <p className="max-w-md text-sm text-[#8b95a5]">{t("sold_copy")}</p>
          </div>
        </Reveal>
        <div className="-mx-5 flex gap-5 overflow-x-auto px-5 pb-2 md:mx-0 md:grid md:grid-cols-4 md:gap-6 md:overflow-visible md:px-0 lg:grid-cols-6" data-testid="sold-strip">
          {data.map((v, i) => {
            const c = coverOf(v);
            return (
              <Reveal key={v.id} delay={i * 0.05} className="w-40 shrink-0 md:w-auto">
                <Link to={`/vehicles/${v.slug}`} data-testid={`sold-item-${v.slug}`} className="group block">
                  <div className="relative aspect-[4/3] overflow-hidden bg-[#14171c]">
                    {c.thumb && <img src={mediaUrl(c.thumb)} alt={c.alt} loading="lazy" className="h-full w-full object-cover grayscale-[35%] transition-[filter,transform] duration-700 group-hover:scale-[1.03] group-hover:grayscale-0" />}
                    <span className="mono absolute left-2 top-2 bg-black/60 px-2 py-1 text-[0.55rem] text-[#e8eaee] backdrop-blur">{t("st_sold")}</span>
                  </div>
                  <p className="mono mt-3 text-[0.58rem] text-[#8b95a5]">{v.make}{v.year ? ` · ${v.year}` : ""}</p>
                  <p className="display-tight mt-1 truncate text-sm text-[#dfe3e8]">{v.model}</p>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const SECTIONS = { positioning: Positioning, featured: Featured, sold: RecentlySold, cinematic: Cinematic, sourcing: SourcingTeaser, network: NetworkSection, sell: SellTeaser, about: AboutTeaser, contact: ContactBlock };

export default function Home() {
  const { lang, bi } = useLang();
  const { data: s } = useSettings();
  useSEO({ title: s ? bi(s.seo_title) : "Palissiere Motor Group", description: s ? bi(s.seo_description) : "", image: s?.og_image, lang, jsonLd: ORG_JSONLD });
  if (!s) return <div className="h-[100svh] bg-[#070809]" />;
  const order = s.section_order?.length ? s.section_order : Object.keys(SECTIONS);
  const withSold = order.includes("sold") ? order : order.flatMap((k) => (k === "featured" ? [k, "sold"] : [k]));
  return (
    <>
      <Hero s={s} />
      {withSold.map((k) => {
        const C = SECTIONS[k];
        return C ? <C key={k} s={s} /> : null;
      })}
    </>
  );
}
